import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, ChevronDown, Instagram } from "lucide-react";

const HASHTAGS =
  "#STAC #STAutoCare #CarDetailing #MobileDetailing #Wolverhampton #WestMidlands #DetailingUK #CarCare #ValetingUK #DetailersOfInstagram";

const PRESETS = [
  {
    id: "full-detail",
    label: "Full detail",
    text: `Another one done right. ✅

Full interior + exterior detail — deep clean, decontaminated and finished with a proper gloss.

📍 Mobile detailing across Wolverhampton & the West Midlands. We come to you.
📅 Book via the link in bio.

${HASHTAGS}`,
  },
  {
    id: "interior",
    label: "Interior",
    text: `Interior brought back to showroom standard. 🧼

Seats, carpets, trim and every crevice — done properly, not rushed.

📍 Wolverhampton & West Midlands | We come to you
📅 Book via the link in bio

${HASHTAGS}`,
  },
  {
    id: "before-after",
    label: "Before / after",
    text: `Swipe for the before. ➡️

This is why you don't settle for a quick wash. Proper products, proper process, proper results.

📍 Mobile across Wolverhampton & the West Midlands
📅 £15 deposit secures your slot — link in bio

${HASHTAGS}`,
  },
  {
    id: "maintenance",
    label: "Maintenance plan",
    text: `Keep it looking like this all year round. 🗓️

Our monthly maintenance plan keeps your car freshly detailed without you lifting a finger — we come to you.

📍 Wolverhampton & West Midlands
📅 Details + booking via the link in bio

${HASHTAGS}`,
  },
];

export const CaptionPresets = () => {
  const [open, setOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (preset) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(preset.text);
      ok = true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = preset.text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
      ta.remove();
    }
    if (ok) {
      setCopiedId(preset.id);
      toast.success("Caption copied — paste it into Instagram.");
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      toast.error("Couldn't copy — long-press the text to copy manually.");
    }
  };

  return (
    <section className="border border-white/10 bg-[#0E0E0E] mb-10" data-testid="caption-presets">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="caption-presets-toggle"
        className="w-full flex items-center justify-between px-6 py-5 text-left"
      >
        <span className="inline-flex items-center gap-3">
          <Instagram size={15} className="text-white/60" />
          <span className="text-xs tracking-[0.22em] uppercase text-white/80">Instagram captions</span>
          <span className="text-[10px] tracking-widest uppercase text-white/40">tap to copy</span>
        </span>
        <ChevronDown
          size={15}
          className={`text-white/50 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border-t border-white/10">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => copy(p)}
              data-testid={`caption-copy-${p.id}`}
              className="group relative bg-[#0E0E0E] hover:bg-[#131313] text-left p-5 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">{p.label}</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-white/45 group-hover:text-white/80 transition-colors">
                  {copiedId === p.id ? <Check size={11} className="text-emerald-300" /> : <Copy size={11} />}
                  {copiedId === p.id ? "Copied" : "Copy"}
                </span>
              </div>
              <p className="text-xs text-white/60 leading-relaxed whitespace-pre-line line-clamp-6">{p.text}</p>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};
